import { api } from "@/api/axios.ts";
import { ApiIdObject, ApiOperationResult } from "@/types/common.types.ts";
import { ApiPagedData } from "@skailer-com/uikit/core";
import { ApiFormEdit } from "@/types/forms/form-edit.types.ts";
import {
  ApiFormTemplate,
  ApiFormTemplateDuplicatePayload,
  ApiFormTemplateFilter,
  ApiFormTemplateMovePayload,
  ApiFormTemplateSavePayload,
} from "@/types/forms/form-template.types.ts";
import { ApiForm, ApiFormView } from "@/types/forms/form-view.types.ts";
import { ReminderSettingsPayload } from "@/types/reminder.types.ts";
import {
  ApiSurvey,
  ApiSurveyAggregatedResults,
  ApiSurveyAggregatedResultsFilter,
  ApiSurveyChart,
  ApiSurveyEdit,
  ApiSurveyExportPayload,
  ApiSurveyFilter,
  ApiSurveyParticipant,
  ApiSurveyParticipantFilter,
  ApiSurveyResultSearchPagedData,
  ApiSurveyResultsFilter,
  ApiSurveySearchItem,
  ApiSurveyUpdateDetails,
  ApiSurveyUpdateForm,
  ApiSurveyUpdateLaunch,
  ApiSurveyReopen,
} from "@/types/survey.types.ts";

export const apiSurveys = {
  create: async function () {
    return api.post<ApiIdObject>("/api/v1/surveys/setup", {});
  },
  duplicate: async function (id: number) {
    return api.post<ApiIdObject>(`/api/v1/surveys/${id}/duplicate`, {});
  },
  updateSurveyDetails: async function (id: number, surveyPayload: ApiSurveyUpdateDetails) {
    return api.put<ApiOperationResult>(`/api/v1/surveys/setup/${id}/details`, surveyPayload);
  },
  updateSurveyForm: async function (id: number, surveyPayload: ApiSurveyUpdateForm) {
    return api.put<ApiOperationResult>(`/api/v1/surveys/setup/${id}/form`, surveyPayload);
  },
  updateSurveyLaunch: async function (id: number, surveyPayload: ApiSurveyUpdateLaunch) {
    return api.put<ApiOperationResult>(`/api/v1/surveys/setup/${id}/launch`, surveyPayload);
  },
  getByIdEdit: async function (id: number) {
    return api.get<ApiSurveyEdit>(`/api/v1/surveys/setup/${id}`);
  },
  search: async function (filter: ApiSurveyFilter, signal: AbortSignal) {
    return api.post<ApiPagedData<ApiSurveySearchItem>>("/api/v1/surveys/search", filter, signal);
  },
  getById: async function (id: number) {
    return api.get<ApiSurvey>(`/api/v1/surveys/${id}`);
  },
  archive: async function (id: number) {
    return api.delete<ApiOperationResult>(`/api/v1/surveys/${id}`);
  },
  getSurveyAggregatedResults: async function (
    id: number,
    filter: ApiSurveyAggregatedResultsFilter
  ) {
    return api.post<ApiSurveyAggregatedResults>(`/api/v1/surveys/${id}/results`, filter);
  },
  exportSurveyResults: async function (id: number, payload: ApiSurveyExportPayload) {
    return api.post<BlobPart>(`/api/v1/surveys/${id}/results/export`, payload, undefined, {
      responseType: "blob",
    });
  },
  participantsSearch: async function (filter: ApiSurveyParticipantFilter, signal: AbortSignal) {
    return api.post<ApiPagedData<ApiSurveyParticipant>>(
      `/api/v1/surveys/${filter.survey_id}/participants/search`,
      filter,
      signal
    );
  },
  questionResultsSearch: async function (filter: ApiSurveyResultsFilter, signal: AbortSignal) {
    return api.post<ApiSurveyResultSearchPagedData>(
      `/api/v1/surveys/${filter.survey_id}/results/${filter.form_element_id}/search`,
      filter,
      signal
    );
  },
  updateReminders: async function (id: number, payload: ReminderSettingsPayload) {
    return api.put<ApiOperationResult>(`/api/v1/surveys/setup/${id}/reminders`, payload);
  },
  launch: async function (id: number) {
    return api.post<ApiOperationResult>(`/api/v1/surveys/${id}/launch`, {});
  },
  pause: async function (id: number) {
    return api.post<ApiOperationResult>(`/api/v1/surveys/${id}/pause`, {});
  },
  complete: async function (id: number) {
    return api.post<ApiOperationResult>(`/api/v1/surveys/${id}/complete`, {});
  },
  reopen: async function (id: number, payload: ApiSurveyReopen) {
    return api.post<ApiOperationResult>(`/api/v1/surveys/${id}/reopen`, payload);
  },
  participantResultByEmployee: async function (surveyId: number, employeeId: number) {
    return api.get<ApiFormView>(`/api/v1/surveys/${surveyId}/participants/${employeeId}/results`);
  },
  formUpdate: async function (id: number, formPayload: ApiFormEdit) {
    return api.put<ApiOperationResult>(`/api/v1/surveys/${id}/form`, formPayload);
  },
  formGetById: async function (id: number) {
    return api.get<ApiForm>(`/api/v1/surveys/${id}/form`);
  },
  searchSurveyTemplates: async function (filter: ApiFormTemplateFilter, signal: AbortSignal) {
    return api.post<ApiPagedData<ApiFormTemplate>>(
      "/api/v1/surveys/templates/search",
      filter,
      signal
    );
  },
  getSurveyTemplateById: async function (id: number) {
    return api.get<ApiFormTemplate>(`/api/v1/surveys/templates/${id}`);
  },
  createSurveyTemplateFromSurvey: async function (id: number, payload: ApiFormTemplateSavePayload) {
    return api.post<ApiFormTemplate>(`/api/v1/surveys/${id}/templates`, payload);
  },
  createSurveyTemplate: async function (payload: ApiFormTemplateSavePayload) {
    return api.post<ApiFormTemplate>(`/api/v1/surveys/templates`, payload);
  },
  duplicateSurveyTemplate: async function (
    id: number,
    payload: ApiFormTemplateDuplicatePayload = {}
  ) {
    return api.post<ApiFormTemplate>(`/api/v1/surveys/templates/${id}/duplicate`, payload);
  },
  updateSurveyTemplate: async function (id: number, payload: ApiFormTemplateSavePayload) {
    return api.put<ApiFormTemplate>(`/api/v1/surveys/templates/${id}`, payload);
  },
  moveSurveyTemplate: async function (id: number, payload: ApiFormTemplateMovePayload) {
    return api.put<ApiOperationResult>(`/api/v1/surveys/templates/${id}/move`, payload);
  },
  deleteSurveyTemplate: async function (id: number) {
    return api.delete<ApiOperationResult>(`/api/v1/surveys/templates/${id}`);
  },
  surveyTemplateFormGetById: async function (id: number) {
    return api.get<ApiForm>(`/api/v1/surveys/templates/${id}/form`);
  },
  surveyTemplateFormUpdate: async function (id: number, formPayload: ApiFormEdit) {
    return api.put<ApiOperationResult>(`/api/v1/surveys/templates/${id}/form`, formPayload);
  },
  getChart: async function (id: number) {
    return api.get<ApiSurveyChart>(`/api/v1/surveys/${id}/chart`);
  },
};
